// The marking of what is on screen (shell-ia §10.2–§10.3; DESIGN-SYSTEM §10.1). WP-SHELL.
// Every signed-in screen is at least its route's floor (`RouteDef.classification`, Internal). A screen, widget, document view, desk or the
// sidebar raises it while mounted with `useClassification(level, source)`; the banner shows the highest level present and its popover
// names each source. Nothing lowers the floor.
import { createContext, useContext, useId, useLayoutEffect, useMemo, useSyncExternalStore, type ReactNode } from "react";
import { SENSITIVITY_ORDER, type ClassificationSource, type Sensitivity } from "@ncp/assistant-sdk/contracts";

export type { Sensitivity, ClassificationSource };

const rank = (s: Sensitivity) => SENSITIVITY_ORDER.indexOf(s);
export const higher = (a: Sensitivity, b: Sensitivity): Sensitivity => (rank(a) >= rank(b) ? a : b);
export const isSensitivity = (v: unknown): v is Sensitivity => typeof v === "string" && (SENSITIVITY_ORDER as readonly string[]).includes(v);

class ClassificationStore {
  private raises = new Map<string, ClassificationSource>();
  private listeners = new Set<() => void>();
  private snapshot: ClassificationSource[] = [];
  subscribe = (l: () => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };
  current = () => this.snapshot;
  set(id: string, raise: ClassificationSource | null) {
    const before = this.raises.get(id);
    if (raise && before && before.level === raise.level && before.source === raise.source) return;
    if (!raise && !before) return;
    if (raise) this.raises.set(id, raise);
    else this.raises.delete(id);
    this.snapshot = [...this.raises.values()];
    for (const l of this.listeners) l();
  }
}

interface Ctx {
  store: ClassificationStore;
  floor: Sensitivity;
  floorSource: string;
}

const ClassificationContext = createContext<Ctx | null>(null);

export function ClassificationProvider({ floor = "internal", floorSource, children }: { floor?: Sensitivity; floorSource: string; children: ReactNode }) {
  const store = useMemo(() => new ClassificationStore(), []);
  const value = useMemo(() => ({ store, floor, floorSource }), [store, floor, floorSource]);
  return <ClassificationContext.Provider value={value}>{children}</ClassificationContext.Provider>;
}

/** A marking as other layers spell it: the KB API's enum names ("Restricted") read the same as the contract's ("restricted"). */
export type SensitivityInput = Sensitivity | Capitalize<Sensitivity> | (string & {});
export const toSensitivity = (v: unknown): Sensitivity | null => (typeof v === "string" && isSensitivity(v.toLowerCase()) ? (v.toLowerCase() as Sensitivity) : null);

/** Raises the banner while the caller is mounted; the highest raise wins; `source` explains it in the banner's popover. */
export function useClassification(level: SensitivityInput | null | undefined, source?: string): void {
  const ctx = useContext(ClassificationContext);
  const id = useId();
  const valid = toSensitivity(level);
  useLayoutEffect(() => {
    ctx?.store.set(id, valid ? { level: valid, source: source ?? "" } : null);
  }, [ctx, id, valid, source]);
  useLayoutEffect(() => () => ctx?.store.set(id, null), [ctx, id]);
}

const NONE: ClassificationSource[] = [];
const noop = () => () => undefined;

/** The level in force (floor and every raise) and the reasons, highest first. */
export function useCurrentClassification(): { level: Sensitivity; sources: ClassificationSource[] } {
  const ctx = useContext(ClassificationContext);
  const raises = useSyncExternalStore(ctx?.store.subscribe ?? noop, ctx?.store.current ?? (() => NONE));
  return useMemo(() => {
    const floor = ctx?.floor ?? "internal";
    const all: ClassificationSource[] = [{ level: floor, source: ctx?.floorSource ?? "" }, ...raises];
    const level = all.reduce<Sensitivity>((m, r) => higher(m, r.level), floor);
    const sources = [...all].sort((a, b) => rank(b.level) - rank(a.level)).filter((s, i, arr) => arr.findIndex((x) => x.level === s.level && x.source === s.source) === i);
    return { level, sources };
  }, [ctx, raises]);
}
